import { useMemo, useState } from "react";
import { useTranslationApp } from "../../../lib/i18next";
import Box from "@mui/material/Box";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableSortLabel from "@mui/material/TableSortLabel";
import TableContainer from "../../Tables/TableContainer";
import TableCell from "../../../pages/BillingAndPayments/components/TableCell";
import {
  BUTTON_ADD,
  COLUMN_NAME_ACTIONS,
  COLUMN_NAME_CLICKS,
  COLUMN_NAME_CPA,
  COLUMN_NAME_CPC,
  COLUMN_NAME_CTR,
  COLUMN_NAME_IMPRESSIONS,
  COLUMN_NAME_KEYWORD,
  COLUMN_NAME_MATCH_TYPE,
  COLUMN_NAME_RESULTS,
  COLUMN_NAME_SEARCH_TERM,
  COLUMN_NAME_SPEND,
  CONVERSION_RATE,
  GOOGLE_SEARCH_TERMS_EMPTY,
  VALUE_NUMBER,
  VALUE_WITH_CURRENCY_MANUAL_FORMAT,
  VALUE_WITH_PERCENTAGE,
} from "../../../i18n/keysTranslations";
import { optionsSearchTermMatchType } from "../../../utils/options";
import { useFormContext } from "react-hook-form";
import { SORT_DIRECTION } from "../../../utils/constants";
import Button from "../../Buttons/Button";

const SEARCH_TERMS_COLUMNS = [
  { key: "searchTerm", label: COLUMN_NAME_SEARCH_TERM, type: "string" },
  { key: "matchType", label: COLUMN_NAME_MATCH_TYPE, type: "string" },
  { key: "keyword", label: COLUMN_NAME_KEYWORD, type: "string" },
  { key: "impressions", label: COLUMN_NAME_IMPRESSIONS, type: "number" },
  { key: "click", label: COLUMN_NAME_CLICKS, type: "number" },
  { key: "ctr", label: COLUMN_NAME_CTR, type: "number" },
  { key: "cpc", label: COLUMN_NAME_CPC, type: "number" },
  { key: "spend", label: COLUMN_NAME_SPEND, type: "number" },
  { key: "conversions", label: COLUMN_NAME_RESULTS, type: "number" },
  { key: "conversionRate", label: CONVERSION_RATE, type: "number" },
  { key: "costPerConversion", label: COLUMN_NAME_CPA, type: "number" },
];

const getMatchTypeLabel = (matchType) =>
  optionsSearchTermMatchType.find(
    (option) => option.value === String(matchType || "").toUpperCase()
  )?.label || matchType;

const getSortValue = ({ row, column, t }) => {
  if (column.key === "matchType") {
    return String(t(getMatchTypeLabel(row.matchType)) || "").toLowerCase();
  }
  if (column.type === "string") {
    return String(row[column.key] || "").toLowerCase();
  }
  return Number(row[column.key]) || 0;
};

const formatCurrencyValue = ({
  cost,
  currency,
  getCostByMainCurrency,
  t,
  digits = 2,
}) => {
  const converted = getCostByMainCurrency
    ? getCostByMainCurrency({ cost, currency })
    : { cost, currency };

  return t(VALUE_WITH_CURRENCY_MANUAL_FORMAT, {
    value: Number(converted.cost || 0).toFixed(digits),
    currency: converted.currency || currency || "",
  });
};

const SearchTermsGoogleTable = ({
  searchTerms = [],
  loading,
  onAddSearchTerm,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);
  const [sortConfig, setSortConfig] = useState({
    key: "impressions",
    direction: SORT_DIRECTION.DESC,
  });
  const { t } = useTranslationApp();
  const { getCostByMainCurrency } = useFormContext() || {};
  const showAddAction = typeof onAddSearchTerm === "function";

  const onChangePage = (newPage) => {
    setPage(newPage);
  };

  const onTableSort = (columnKey) => {
    setSortConfig((prevConfig) => ({
      key: columnKey,
      direction:
        prevConfig.key === columnKey &&
        prevConfig.direction === SORT_DIRECTION.DESC
          ? SORT_DIRECTION.ASC
          : SORT_DIRECTION.DESC,
    }));
    setPage(0);
  };

  const sortedSearchTerms = useMemo(() => {
    const column = SEARCH_TERMS_COLUMNS.find(
      (item) => item.key === sortConfig.key
    );
    if (!column) {
      return searchTerms;
    }

    return [...searchTerms].sort((a, b) => {
      const aValue = getSortValue({ row: a, column, t });
      const bValue = getSortValue({ row: b, column, t });
      const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      return sortConfig.direction === SORT_DIRECTION.ASC
        ? comparison
        : -comparison;
    });
  }, [searchTerms, sortConfig, t]);

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
      <TableContainer
        page={page}
        loading={loading}
        hasMarginBottom={false}
        emptyText={GOOGLE_SEARCH_TERMS_EMPTY}
        onPageChange={onChangePage}
        rowsPerPage={rowsPerPage}
        keys={sortedSearchTerms}
        sx={{
          maxHeight: "calc(100vh - 400px)",
          borderRadius: "0px !important",
        }}
      >
        <TableHead>
          <TableRow>
            {SEARCH_TERMS_COLUMNS.map((column) => (
              <TableCell key={column.key} isHeader={true}>
                <TableSortLabel
                  active={sortConfig.key === column.key}
                  direction={
                    sortConfig.key === column.key
                      ? sortConfig.direction
                      : SORT_DIRECTION.ASC
                  }
                  onClick={() => onTableSort(column.key)}
                  sx={{
                    ".MuiTableSortLabel-icon": {
                      fontSize: 15,
                    },
                  }}
                >
                  {t(column.label)}
                </TableSortLabel>
              </TableCell>
            ))}
            {showAddAction && (
              <TableCell isHeader={true} resizable={false}>
                {t(COLUMN_NAME_ACTIONS)}
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {(rowsPerPage > 0
            ? sortedSearchTerms.slice(
                page * rowsPerPage,
                page * rowsPerPage + rowsPerPage
              )
            : sortedSearchTerms
          ).map((searchTerm, index) => {
            const isLastRow = index === sortedSearchTerms.length - 1;
            const matchTypeLabel = getMatchTypeLabel(searchTerm.matchType);

            return (
              <TableRow
                key={`${searchTerm.searchTerm}-${searchTerm.matchType}-${searchTerm.keyword}-${index}`}
              >
                <TableCell
                  indexRow={index}
                  isLastRow={isLastRow}
                  sx={{ whiteSpace: "pre-wrap" }}
                >
                  {searchTerm.searchTerm}
                </TableCell>
                <TableCell
                  indexRow={index}
                  isLastRow={isLastRow}
                  sx={{ whiteSpace: "pre-wrap" }}
                >
                  {t(matchTypeLabel)}
                </TableCell>
                <TableCell
                  indexRow={index}
                  isLastRow={isLastRow}
                  sx={{ whiteSpace: "pre-wrap" }}
                >
                  {searchTerm.keyword}
                </TableCell>
                <TableCell indexRow={index} isLastRow={isLastRow}>
                  {t(VALUE_NUMBER, { value: searchTerm.impressions || 0 })}
                </TableCell>
                <TableCell indexRow={index} isLastRow={isLastRow}>
                  {t(VALUE_NUMBER, { value: searchTerm.click || 0 })}
                </TableCell>
                <TableCell indexRow={index} isLastRow={isLastRow}>
                  {t(VALUE_WITH_PERCENTAGE, { value: searchTerm.ctr || 0 })}
                </TableCell>
                <TableCell indexRow={index} isLastRow={isLastRow}>
                  {formatCurrencyValue({
                    cost: searchTerm.cpc,
                    currency: searchTerm.currency,
                    getCostByMainCurrency,
                    t,
                  })}
                </TableCell>
                <TableCell indexRow={index} isLastRow={isLastRow}>
                  {formatCurrencyValue({
                    cost: searchTerm.spend,
                    currency: searchTerm.currency,
                    getCostByMainCurrency,
                    t,
                  })}
                </TableCell>
                <TableCell indexRow={index} isLastRow={isLastRow}>
                  {t(VALUE_NUMBER, { value: searchTerm.conversions || 0 })}
                </TableCell>
                <TableCell indexRow={index} isLastRow={isLastRow}>
                  {t(VALUE_WITH_PERCENTAGE, {
                    value: searchTerm.conversionRate || 0,
                  })}
                </TableCell>
                <TableCell indexRow={index} isLastRow={isLastRow}>
                  {formatCurrencyValue({
                    cost: searchTerm.costPerConversion,
                    currency: searchTerm.currency,
                    getCostByMainCurrency,
                    t,
                  })}
                </TableCell>
                {showAddAction && (
                  <TableCell indexRow={index} isLastRow={isLastRow}>
                    <Button
                      color="secondary"
                      onClick={() => onAddSearchTerm(searchTerm)}
                      sx={{
                        minWidth: "auto",
                        height: 36,
                        px: 1.5,
                        py: 0,
                      }}
                    >
                      {t(BUTTON_ADD)}
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </TableContainer>
    </Box>
  );
};

export default SearchTermsGoogleTable;
